import * as z from 'zod'
import { userTrialPeriodSchema } from '../UserTrialPeriod.js'
import { USER_CATEGORIES, USER_CATEGORY_FILTER_VALUES } from '../UserCategory.js'
import { DUserListItem, UserListItem } from '../Users.js'
import { PaginatedResponse } from '../PaginatedResponse.js'
import { DeleteApiContract, GetApiContract, PatchApiContract, PostApiContract } from './ApiContract.js'
import { DUserAdminDetail, UserAdminDetail, UserMe } from '../Users.js'
import { userSettingsSchema } from '../UsersSettings.js'
import { ShallowDehydrateObject } from 'kysely'

export interface GetUserMeRequest extends GetApiContract {
    Response: UserMe
    ResponseJson: UserMe
}

export const USER_LIST_SORT_FIELDS = [
    'name',
    'email',
    'customerName',
    'userCategory',
    'lastActive',
    'deviceCount',
] as const

export type UserListSortField = (typeof USER_LIST_SORT_FIELDS)[number]

export const getUserListQuerySchema = z.object({
    customerId: z.string().optional(),
    page: z.coerce.number(),
    limit: z.coerce.number().max(100),
    userCategory: z.enum(USER_CATEGORY_FILTER_VALUES).optional(),
    sortField: z.enum(USER_LIST_SORT_FIELDS).optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
})

export type UserListQueryParameters = z.infer<typeof getUserListQuerySchema>

export interface GetUserListRequest extends GetApiContract {
    Response: PaginatedResponse<DUserListItem>
    ResponseJson: PaginatedResponse<UserListItem>
    QueryParameters: UserListQueryParameters
}

export interface GetUserByIdRequest extends GetApiContract {
    Response: DUserAdminDetail
    ResponseJson: UserAdminDetail
}

export interface GetUserMeEncryptionKeyRequest extends GetApiContract {
    Response: { encryptionKey: string }
    ResponseJson: { encryptionKey: string }
}

export const patchUserMeBodySchema = z.object({
    password: z
        .object({
            currentPassword: z.string(),
            newPassword: z.string(),
        })
        .optional(),
    settings: userSettingsSchema.optional(),
})

export interface PatchUserMeRequest extends PatchApiContract {
    Body: z.infer<typeof patchUserMeBodySchema>
    Response: UserMe
    ResponseJson: UserMe
}

export const patchUserBodySchema = z
    .object({
        customerId: z.string().optional().nullable(),
        disabled: z.boolean().optional(),
        password: z.string().optional(),
        userCategory: z.enum(USER_CATEGORIES).optional(),
        trialPeriod: userTrialPeriodSchema.optional(),
    })
    .superRefine((data, ctx) => {
        if (data.userCategory === 'trial' && !data.trialPeriod) {
            ctx.addIssue({
                code: 'custom',
                message: 'trialPeriod is required when userCategory is trial',
                path: ['trialPeriod'],
            })
        }
        if (data.trialPeriod && data.userCategory !== undefined && data.userCategory !== 'trial') {
            ctx.addIssue({
                code: 'custom',
                message: 'trialPeriod is only allowed when userCategory is trial',
                path: ['trialPeriod'],
            })
        }
    })

export interface PatchUserRequest extends PatchApiContract {
    Body: z.infer<typeof patchUserBodySchema>
    Response: { message: string }
    ResponseJson: { message: string }
}

export const postUserBodySchema = z
    .object({
        email: z.email(),
        name: z.string(),
        password: z.string(),
        customerId: z.string().optional().nullable(),
        userCategory: z.enum(USER_CATEGORIES).optional(),
        trialPeriod: userTrialPeriodSchema.optional(),
    })
    .superRefine((data, ctx) => {
        if (data.userCategory === 'trial' && !data.trialPeriod) {
            ctx.addIssue({
                code: 'custom',
                message: 'trialPeriod is required when userCategory is trial',
                path: ['trialPeriod'],
            })
        }
        if (data.trialPeriod && data.userCategory !== 'trial') {
            ctx.addIssue({
                code: 'custom',
                message: 'trialPeriod is only allowed when userCategory is trial',
                path: ['trialPeriod'],
            })
        }
    })

export interface PostUserRequest<
    R = {
        user: {
            userId: string | null
            username: string
            email: string
            name: string
            createdAt?: Date
        }
    },
> extends PostApiContract {
    Body: z.infer<typeof postUserBodySchema>
    Response: R
    ResponseJson: ShallowDehydrateObject<R>
}

export const postUserRequestResetPassword = z.object({
    email: z.string(),
})

export interface PostUserRequestResetPassword extends PostApiContract {
    Body: z.infer<typeof postUserRequestResetPassword>
}

export const postUserPasswordSchema = z.object({
    code: z.string(),
    email: z.string(),
    password: z.string(),
})

export interface PostUserResetPassword extends PostApiContract {
    Body: z.infer<typeof postUserPasswordSchema>
}

export interface DeleteUserRequest extends DeleteApiContract {
    Response: { message: string }
    ResponseJson: { message: string }
}
